import React from 'react';

import { Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { orange, red } from '@mui/material/colors';

import MarginWarningCell from './MarginWarningCell';
import { MIN_MARGIN_PCT, calcMargin, isGrossBelowBase, isMarginBelowMinimum, netToAud } from './marginRules';
import { BASIC_CATEGORY_INDEX, CELL_SX, HEADER_SX, PACKAGE_CATEGORIES } from './packageCategories';
import type { CategoryRoom, RoomPricing } from './types';

const EXTRA_NIGHT_DIVIDER_SX = { borderLeft: '3px solid', borderLeftColor: orange[700] };

const LOS_COL_WIDTH = 110;
const ROOM_TYPE_LABEL_COL_WIDTH = 60;
const ROOM_TYPE_NAME_COL_WIDTH = 220;

type MarginTableProps = {
  audToSourceCurrencyRate: Record<string, number | undefined>;
  netCurrency: string;
  pricingByRoomId: Record<number, RoomPricing>;
  roomsByCategory: Array<Array<CategoryRoom>>;
  sortedLos: Array<number>;
};

export default function MarginTable({
  audToSourceCurrencyRate,
  netCurrency,
  pricingByRoomId,
  roomsByCategory,
  sortedLos,
}: MarginTableProps) {
  const toAud = (net: number) => netToAud(net, netCurrency, audToSourceCurrencyRate);

  // Non-basic packages compare against the basic package for the same asset name.
  const basicGrossByAsset: Record<
    string,
    { extraNight: number | undefined; byLos: Record<number, number | undefined> }
  > = {};
  roomsByCategory[BASIC_CATEGORY_INDEX].forEach(({ id, assetName }) => {
    const pricing = pricingByRoomId[id];
    const byLos: Record<number, number | undefined> = {};
    sortedLos.forEach((nights) => {
      byLos[nights] = calcMargin(
        Number(pricing?.sellByLos[nights]) || 0,
        toAud(Number(pricing?.netByLos[nights]) || 0),
      )?.gross;
    });
    basicGrossByAsset[assetName] = {
      byLos,
      extraNight: calcMargin(Number(pricing?.extraNightSell) || 0, toAud(Number(pricing?.extraNightNet) || 0))?.gross,
    };
  });

  const grossColWidth = sortedLos.length === 1 ? 190 : LOS_COL_WIDTH;
  const marginTableWidth =
    ROOM_TYPE_LABEL_COL_WIDTH +
    ROOM_TYPE_NAME_COL_WIDTH +
    sortedLos.length * grossColWidth +
    sortedLos.length * LOS_COL_WIDTH +
    130 +
    110;

  return (
    <TableContainer sx={{ mt: 2, maxWidth: '100%', overflowX: 'auto', width: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: marginTableWidth }}>
        <colgroup>
          <col style={{ width: ROOM_TYPE_LABEL_COL_WIDTH }} />
          <col style={{ width: ROOM_TYPE_NAME_COL_WIDTH }} />
          {sortedLos.map((nights) => (
            <col key={`gross-col-${nights}`} style={{ width: grossColWidth }} />
          ))}
          {sortedLos.map((nights) => (
            <col key={`margin-col-${nights}`} style={{ width: LOS_COL_WIDTH }} />
          ))}
          <col style={{ width: 130 }} />
          <col style={{ width: 110 }} />
        </colgroup>
        <TableBody>
          <TableRow>
            <TableCell colSpan={2} sx={CELL_SX} />
            <TableCell align="center" colSpan={sortedLos.length} sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
              GROSS MARGIN
            </TableCell>
            <TableCell align="center" colSpan={sortedLos.length} sx={HEADER_SX}>
              MARGIN %
            </TableCell>
            <TableCell align="center" colSpan={2} sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, color: orange[700] }}>
              EXTRA NIGHT
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center" colSpan={2} sx={HEADER_SX}>
              ROOM TYPE
            </TableCell>
            {sortedLos.map((nights) => (
              <TableCell align="center" key={`gross-${nights}`} sx={HEADER_SX}>
                {nights}N
              </TableCell>
            ))}
            {sortedLos.map((nights) => (
              <TableCell align="center" key={`margin-${nights}`} sx={HEADER_SX}>
                {nights}N
              </TableCell>
            ))}
            <TableCell align="center" sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, whiteSpace: 'nowrap' }}>
              GROSS (AUD)
            </TableCell>
            <TableCell align="center" sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
              MARGIN %
            </TableCell>
          </TableRow>
          {PACKAGE_CATEGORIES.map((category, categoryIndex) => {
            const rooms = roomsByCategory[categoryIndex];
            // Upgrade/Solo categories start empty and only appear once a room type is added to them.
            if (categoryIndex !== BASIC_CATEGORY_INDEX && rooms.every((room) => !room.assetName)) {
              return null;
            }
            const rowCellSx = { ...CELL_SX, bgcolor: category.bgColor };
            const labelSx = {
              ...rowCellSx,
              color: category.labelColor,
              transform: 'rotate(180deg)',
              writingMode: 'vertical-lr' as const,
            };

            return rooms.map(({ id, assetName }, index) => {
              const pricing = pricingByRoomId[id];
              const basicGross = categoryIndex === BASIC_CATEGORY_INDEX ? undefined : basicGrossByAsset[assetName];

              return (
                <TableRow key={id}>
                  {index === 0 && (
                    <TableCell align="center" rowSpan={rooms.length} sx={labelSx}>
                      {category.label}
                    </TableCell>
                  )}
                  <TableCell sx={rowCellSx}>{assetName || '—'}</TableCell>
                  {sortedLos.map((nights) => {
                    const sell = Number(pricing?.sellByLos[nights]) || 0;
                    const net = toAud(Number(pricing?.netByLos[nights]) || 0);
                    const margin = calcMargin(sell, net);
                    const baseGross = basicGross?.byLos[nights];
                    const belowBasic = margin !== undefined && isGrossBelowBase(sell, margin.gross, baseGross);
                    return (
                      <MarginWarningCell
                        color={orange[800]}
                        key={`gross-value-${nights}`}
                        reason={
                          belowBasic && baseGross !== undefined
                            ? `Gross margin is below the base package gross margin ($${baseGross.toFixed(2)}) for ${nights}N`
                            : null
                        }
                        sx={rowCellSx}
                        value={margin === undefined ? '—' : `$${margin.gross.toFixed(2)}`}
                      />
                    );
                  })}
                  {sortedLos.map((nights) => {
                    const sell = Number(pricing?.sellByLos[nights]) || 0;
                    const net = toAud(Number(pricing?.netByLos[nights]) || 0);
                    const margin = calcMargin(sell, net);
                    return (
                      <MarginWarningCell
                        color={red[700]}
                        key={`margin-value-${nights}`}
                        reason={
                          isMarginBelowMinimum(sell, margin?.pct)
                            ? `Margin is below the ${MIN_MARGIN_PCT}% minimum`
                            : null
                        }
                        sx={rowCellSx}
                        value={margin === undefined ? '—' : `${margin.pct.toFixed(1)}%`}
                      />
                    );
                  })}
                  {(() => {
                    const extraSell = Number(pricing?.extraNightSell) || 0;
                    const extraNet = toAud(Number(pricing?.extraNightNet) || 0);
                    const margin = calcMargin(extraSell, extraNet);
                    const baseExtraGross = basicGross?.extraNight;
                    const belowBasic =
                      margin !== undefined && isGrossBelowBase(extraSell, margin.gross, baseExtraGross);
                    return (
                      <>
                        <MarginWarningCell
                          color={orange[800]}
                          reason={
                            belowBasic && baseExtraGross !== undefined
                              ? `Extra night gross margin is below the base package extra night gross margin ($${baseExtraGross.toFixed(2)})`
                              : null
                          }
                          sx={{ ...rowCellSx, ...EXTRA_NIGHT_DIVIDER_SX }}
                          value={margin === undefined ? '—' : `$${margin.gross.toFixed(2)}`}
                        />
                        <MarginWarningCell
                          color={red[700]}
                          reason={
                            isMarginBelowMinimum(extraSell, margin?.pct)
                              ? `Margin is below the ${MIN_MARGIN_PCT}% minimum`
                              : null
                          }
                          sx={rowCellSx}
                          value={margin === undefined ? '—' : `${margin.pct.toFixed(1)}%`}
                        />
                      </>
                    );
                  })()}
                </TableRow>
              );
            });
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
